"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Compatibility() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const checkItems = ["Overall arena dimensions and outer shape", "Center opening and bull-base mounting interface", "Blower requirements", "Attachment points, anchoring, and safety tethering"];
  return <section id="inflatablereplacementcompatibility" data-nav="light" className="relative w-full bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="space-y-12">
          {/* Header Block */}
          <motion.div variants={itemVariants} className="border-l-4 border-primary pl-4 sm:pl-6">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground uppercase">
              Can You Replace the Inflatable Without Replacing the Bull?
            </h2>
          </motion.div>

          {/* Primary Statement / Callout */}
          <motion.div variants={itemVariants} className="border border-border bg-card p-6 sm:p-8 shadow-xs rounded-none">
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-muted text-foreground shrink-0 rounded-none border border-border">
                <Wrench className="w-5 h-5 text-primary" />
              </div>
              <p className="text-base sm:text-lg text-foreground font-normal leading-relaxed">
                In many systems, the inflatable safety arena is a separate component from the powered bull. A compatible replacement may therefore be possible without replacing the complete machine.
              </p>
            </div>
          </motion.div>

          {/* Compatibility Details Grid */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                Technical Criteria
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Compatibility still matters. Compare the overall dimensions, shape, center opening or bull-base interface, blower requirements and any relevant attachment points before choosing another inflatable.
              </p>

              <ul className="space-y-2.5 pt-2">
                {checkItems.map((spec, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>{spec}</span>
                  </li>)}
              </ul>
            </div>

            {/* Warning Box */}
            <div className="border border-destructive/30 bg-destructive/5 p-6 sm:p-7 rounded-none space-y-3">
              <div className="flex items-center gap-2 text-destructive font-mono text-xs uppercase tracking-wider font-semibold">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Interchangeability Warning</span>
              </div>
              <p className="text-base font-semibold text-foreground leading-snug">
                Do not assume two similar-looking inflatables are interchangeable.
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Compare these details carefully rather than assuming two similar-looking inflatables will fit the same way.
              </p>
            </div>
          </motion.div>

          {/* Action CTA */}
          <motion.div variants={itemVariants} className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="space-y-1">
              <p className="text-sm font-semibold uppercase tracking-wide text-foreground">
                Need a replacement cushion or safety arena?
              </p>
              <p className="text-xs text-muted-foreground">
                Check our Parts & Accessories catalog for available inflatable components, blowers and related replacement equipment. Confirm compatibility with the exact bull and inflatable before ordering.
              </p>
            </div>

            <Button asChild variant="default" size="lg" className="rounded-none uppercase tracking-wider font-semibold px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
              <Link to="/MechanicalBullPartsAndAccessoriesPage" className="inline-flex items-center gap-2">
                <span>Browse Parts & Accessories</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}